/**
 * The order being captured, and the single place the pricing engine is driven from.
 *
 * Framework-free like the engine it wraps, for the same reason: the interesting behaviour —
 * "does adding 10 units to drop 2 earn the tier?" — is testable without a component, a
 * fixture or a browser.
 *
 * It owns the quantities, the delivery columns and the one clock read in the whole flow
 * (`asOf`, captured when the draft is created). Every mutation re-prices immediately and
 * caches the result, so the matrix reads `draft.priced` synchronously during change detection
 * instead of subscribing to anything.
 *
 * See docs/features/order-capture.md.
 */

import {
  AppUiFieldValue,
  AppUiFieldValues,
} from '@omni/interfaces/app-ui/record.interface';
import {
  DeliveryInput,
  OrderLineInput,
  PricedOrder,
} from './pricing/pricing.model';
import { PricingPriceList } from '../../interfaces/pricing/pricing-bundle.interface';
import { priceOrder } from './pricing/price-order';

/** Dataverse accepts a supplied primary key, so a retried create cannot duplicate. */
const newGuid = (): string => crypto.randomUUID();

export interface OrderDraftContext {
  /** Resolved from the cached bundle before the draft is built. */
  priceList: PricingPriceList;
  /** Validity date for the whole draft, ISO `yyyy-MM-dd`. Captured once, never re-read. */
  asOf: string;
  bundleVersion?: string | null;
  maxDiscountPct?: number;
  /** The price list's Max Discount Amount, for the whole order. */
  maxDiscountAmount?: number;
  /** Quota already consumed by this account's earlier orders. */
  priorQuantityByProduct?: Record<string, number>;
  includeUnmapped?: boolean;
  /** An order read back to be viewed: Dynamics' own figures, never priced again on the device. */
  fixedPricing?: PricedOrder;
}

/** Per-delivery editable header state. */
export interface OrderDraftDelivery extends DeliveryInput {
  /** Minted on the device and kept for the record's whole life; the server stores it as the key. */
  id: string;
  /** The first drop is the primary one — it carries the star in the design. */
  isPrimary: boolean;
  /** The rep picked this drop's date: saved even with no quantities, so another device dates it the same. */
  datePicked?: boolean;
}

export interface DeliveryContent {
  product: string;
  units: number;
  free: number;
}

/** The delivery schedule the rep chose. `key` is the record id, or its name while the bundle sends empty ids. */
export interface OrderDraftSchedule {
  key: string;
  name: string;
  /** Order columns copied from the schedule at pick time; null means "clear it on the order". */
  columns?: Record<string, unknown>;
  /** Which of `columns` holds the delivery count, kept equal to the order's drops. */
  countColumn?: string;
  /**
   * The drops the order holds, on the device only: a schedule with no count (a rolling one) leaves
   * the order nowhere to keep the deliveries the rep added or removed.
   */
  drops?: number;
}

/** Everything needed to rebuild a draft from a saved order or an offline queue entry. */
export interface OrderDraftSnapshot {
  /** The `salesorder` id, minted on the device so a retried create cannot duplicate. */
  orderId: string;
  /** `salesorderdetail` ids by product then delivery sequence, minted when a cell is first typed. */
  lineIds: Record<string, Record<number, string>>;
  /** Every line id a queued request has carried, so a later request can name the ones removed. */
  sentLineIds?: string[];
  asOf: string;
  priceListId: string;
  bundleVersion: string | null;
  deliveries: OrderDraftDelivery[];
  lines: OrderLineInput[];
  /** Header field values, keyed by `salesorder` attribute. */
  header: AppUiFieldValues;
  schedule?: OrderDraftSchedule | null;
  /** The priced net when saved, so a list of saved orders can show it without repricing. */
  net?: number;
  /** Its price list's ISO currency code, for the same lists' money; absent on older records. */
  currency?: string | null;
  /** The first product ordered, by name, for the same lists; absent on older records. */
  firstProduct?: string;
}

/** A line the rep ordered something on, in any delivery. */
export function isOrdered(line: Pick<OrderLineInput, 'quantities'>): boolean {
  return Object.values(line.quantities ?? {}).some((quantity) => quantity > 0);
}

/** The delivery sequences holding a quantity: a schedule lays out more drops than the rep fills. */
export function filledSequences(
  lines: readonly Pick<OrderLineInput, 'quantities'>[] | undefined,
): Set<number> {
  const filled = new Set<number>();
  for (const line of lines ?? [])
    for (const [sequence, quantity] of Object.entries(line.quantities ?? {}))
      if (quantity > 0) filled.add(Number(sequence));
  return filled;
}

export class OrderDraft {
  /** product -> delivery sequence -> quantity. Sparse: an absent entry is zero. */
  private readonly quantities = new Map<string, Map<number, number>>();
  private readonly manualPct = new Map<string, number>();
  private readonly manualAmount = new Map<string, number>();

  /**
   * Header values by attribute. Held here rather than in the page because an order is one
   * thing: a draft saved offline and reopened has to bring its header back with its lines.
   */
  private readonly headerValues = new Map<string, AppUiFieldValue>();

  private orderId = newGuid();
  private readonly lineIds = new Map<string, Map<number, string>>();
  private readonly sentLineIdSet = new Set<string>();
  private deliveryList: OrderDraftDelivery[] = [];
  private nextSequence = 1;
  private chosenSchedule: OrderDraftSchedule | null = null;

  private cached: PricedOrder;
  /** Inside `batch`: writes mark the pricing stale, and it is worked out once at the end. */
  private batchDepth = 0;
  private repriceDeferred = false;

  constructor(private readonly context: OrderDraftContext) {
    // An order always has at least one drop; a matrix with no columns has nothing to type into.
    // Pushed directly rather than through `addDelivery` so the cache is definitely assigned.
    this.deliveryList.push({
      id: newGuid(),
      sequence: this.nextSequence++,
      date: null,
      shipTo: null,
      isPrimary: true,
    });
    this.cached = this.computePriced();
  }

  public get priceList(): PricingPriceList {
    return this.context.priceList;
  }

  public get asOf(): string {
    return this.context.asOf;
  }

  /** The most a line may take off, from the price list; absent, no limit. */
  public get maxDiscountPct(): number | undefined {
    return this.context.maxDiscountPct;
  }

  public get deliveries(): readonly OrderDraftDelivery[] {
    return this.deliveryList;
  }

  /** A delivery's number on the Order tab, from 1; null once the order no longer holds it. */
  public deliveryNumber(sequence: number): number | null {
    const index = this.deliveryList.findIndex((d) => d.sequence === sequence);
    return index < 0 ? null : index + 1;
  }

  public get schedule(): OrderDraftSchedule | null {
    return this.chosenSchedule;
  }

  public get id(): string {
    return this.orderId;
  }

  public get sentLineIds(): readonly string[] {
    return [...this.sentLineIdSet];
  }

  /** The current priced result. Recomputed on mutation, never on read. */
  public get priced(): PricedOrder {
    return this.cached;
  }

  /** Every captured header value, for the summary and for persistence. */
  public get header(): AppUiFieldValues {
    const values: AppUiFieldValues = {};
    for (const [attribute, value] of this.headerValues)
      values[attribute] = { ...value };
    return values;
  }

  /** Any quantity typed, whatever else the draft holds. */
  public get hasQuantities(): boolean {
    return this.quantities.size > 0;
  }

  /**
   * Rebuilds a draft from a snapshot — editing a saved order, or replaying a queued one.
   * The snapshot's `asOf` is restored rather than refreshed, so re-opening a draft does not
   * silently re-price it against a different day.
   */
  public static fromSnapshot(
    context: OrderDraftContext,
    snapshot: OrderDraftSnapshot,
  ): OrderDraft {
    const draft = new OrderDraft({ ...context, asOf: snapshot.asOf });
    draft.deliveryList = [];
    draft.nextSequence = 1;
    if (snapshot.orderId) draft.orderId = snapshot.orderId;
    draft.chosenSchedule = snapshot.schedule ?? null;
    for (const id of snapshot.sentLineIds ?? []) draft.sentLineIdSet.add(id);
    draft.restoreLineIds(snapshot.lineIds ?? {});

    for (const delivery of snapshot.deliveries) {
      // A snapshot from before ids existed gets one now; it stays from here on.
      draft.deliveryList.push({ ...delivery, id: delivery.id ?? newGuid() });
      draft.nextSequence = Math.max(draft.nextSequence, delivery.sequence + 1);
    }
    if (draft.deliveryList.length === 0) draft.addDelivery();

    for (const [attribute, value] of Object.entries(snapshot.header ?? {})) {
      draft.headerValues.set(attribute, { ...value });
    }

    for (const line of snapshot.lines) draft.restoreLine(line);

    draft.reprice();
    return draft;
  }

  public setSchedule(schedule: OrderDraftSchedule | null): void {
    this.chosenSchedule = schedule
      ? {
          ...schedule,
          ...(schedule.columns ? { columns: { ...schedule.columns } } : {}),
        }
      : null;
    this.syncCount();
  }

  public clearQuantities(): void {
    this.quantities.clear();
    this.reprice();
  }

  /** The drops past the first `count` that hold quantities, and would lose them to `applyDeliveryDates`. */
  public filledDeliveriesBeyond(count: number): number[] {
    return this.deliveryList
      .slice(count)
      .filter((delivery) => this.hasQuantityIn(delivery.sequence))
      .map((delivery) => delivery.sequence);
  }

  /** What a drop holds: each product's units, and the free units it earned there. */
  public deliveryContents(sequence: number): DeliveryContent[] {
    const contents = new Map<string, DeliveryContent>();
    for (const [product, bySequence] of this.quantities) {
      const units = bySequence.get(sequence) ?? 0;
      if (units > 0) contents.set(product, { product, units, free: 0 });
    }
    for (const line of this.priced.lines) {
      if (!line.isFreeGood) continue;
      const free =
        line.perDelivery.find((cell) => cell.sequence === sequence)?.quantity ??
        0;
      if (free <= 0) continue;
      const held = contents.get(line.product);
      contents.set(line.product, {
        product: line.product,
        units: held?.units ?? 0,
        free: (held?.free ?? 0) + free,
      });
    }
    return [...contents.values()];
  }

  /**
   * Re-dates the order to `dates`, one drop each, keeping quantities by position: drop 1 keeps
   * drop 1's quantities whatever its new date. Drops past the new count go, with their quantities.
   */
  public applyDeliveryDates(dates: readonly string[]): void {
    if (dates.length === 0) return;
    for (const dropped of this.deliveryList.slice(dates.length))
      this.clearQuantitiesIn(dropped.sequence);
    this.deliveryList = this.deliveryList.slice(0, dates.length);
    this.deliveryList.forEach((delivery, index) => {
      delivery.date = dates[index];
      delete delivery.datePicked;
    });
    for (let index = this.deliveryList.length; index < dates.length; index++) {
      this.deliveryList.push({
        id: newGuid(),
        sequence: this.nextSequence++,
        date: dates[index],
        shipTo: this.deliveryList[0]?.shipTo ?? null,
        isPrimary: false,
      });
    }
    this.deliveryList.forEach(
      (delivery, position) => (delivery.isPrimary = position === 0),
    );
    this.syncCount();
    this.reprice();
  }

  /**
   * Adds an empty drop on each of `dates` the order has none on, then puts the drops in date
   * order. A drop keeps its sequence and quantities, so a saved order regains the slots it left empty.
   */
  public fillDeliveryDates(dates: readonly string[]): void {
    const held = new Set(this.deliveryList.map((delivery) => delivery.date));
    // Priced once, after the sort: the primary drop is the earliest.
    this.batch(() => {
      for (const date of dates) if (!held.has(date)) this.addDelivery(date);
      this.deliveryList.sort((a, b) =>
        (a.date ?? '\uffff').localeCompare(b.date ?? '\uffff'),
      );
      this.deliveryList.forEach(
        (delivery, position) => (delivery.isPrimary = position === 0),
      );
      this.syncCount();
      this.reprice();
    });
  }

  /**
   * Remembers ids a request carried. Kept even after they are removed: a request may be
   * dropped before it is sent, and the next one must still name the removal.
   */
  public rememberSent(ids: readonly string[]): void {
    for (const id of ids) this.sentLineIdSet.add(id);
  }

  /** The line id for one cell, minted on first use and kept — a retry must name the same line. */
  public lineId(product: string, sequence: number): string {
    const bySequence = this.lineIds.get(product) ?? new Map<number, string>();
    let id = bySequence.get(sequence);
    if (!id) {
      id = newGuid();
      bySequence.set(sequence, id);
      this.lineIds.set(product, bySequence);
    }
    return id;
  }

  public getQuantity(product: string, sequence: number): number {
    return this.quantities.get(product)?.get(sequence) ?? 0;
  }

  /** Combined across every drop — the quantity discounts are evaluated on. */
  public getCombinedQuantity(product: string): number {
    let total = 0;
    for (const quantity of this.quantities.get(product)?.values() ?? [])
      total += quantity;
    return total;
  }

  /**
   * Runs `work`, pricing the order once at the end rather than after every write: a copy across 52
   * weekly deliveries is a thousand writes. `priced` is stale until `work` returns.
   */
  public batch<T>(work: () => T): T {
    this.batchDepth++;
    try {
      return work();
    } finally {
      this.batchDepth--;
      if (this.batchDepth === 0 && this.repriceDeferred) {
        this.repriceDeferred = false;
        this.reprice();
      }
    }
  }

  /** Returns true when the value actually changed, so a caller can skip a repaint. */
  public setQuantity(
    product: string,
    sequence: number,
    quantity: number,
  ): boolean {
    if (this.getQuantity(product, sequence) === quantity) return false;
    this.writeQuantity(product, sequence, quantity);
    this.reprice();
    return true;
  }

  public setManualDiscountPct(
    product: string,
    percentage: number | null,
  ): void {
    if (percentage === null) this.manualPct.delete(product);
    else this.manualPct.set(product, percentage);
    this.manualAmount.delete(product);
    this.reprice();
  }

  public setManualDiscountAmount(product: string, amount: number | null): void {
    if (amount === null) this.manualAmount.delete(product);
    else this.manualAmount.set(product, amount);
    this.manualPct.delete(product);
    this.reprice();
  }

  /** Appends a drop and returns its sequence. It ships where the first drop does unless told otherwise. */
  public addDelivery(
    date: string | null = null,
    shipTo: string | null = this.deliveryList[0]?.shipTo ?? null,
  ): number {
    const sequence = this.nextSequence++;
    this.deliveryList.push({
      id: newGuid(),
      sequence,
      date,
      shipTo,
      isPrimary: this.deliveryList.length === 0,
    });
    this.syncCount();
    this.reprice();
    return sequence;
  }

  /**
   * Removes a drop, and with it every quantity typed into that column.
   *
   * The last drop cannot be removed — an order with no delivery column has nowhere to enter a
   * quantity, and the engine would price an empty order.
   */
  public removeDelivery(sequence: number): boolean {
    if (this.deliveryList.length <= 1) return false;
    const index = this.deliveryList.findIndex((d) => d.sequence === sequence);
    if (index < 0) return false;

    this.deliveryList.splice(index, 1);
    this.clearQuantitiesIn(sequence);
    // Promote the new first drop so exactly one stays primary.
    this.deliveryList.forEach(
      (delivery, position) => (delivery.isPrimary = position === 0),
    );
    this.syncCount();
    this.reprice();
    return true;
  }

  public setDeliveryDate(sequence: number, date: string | null): void {
    const delivery = this.deliveryList.find((d) => d.sequence === sequence);
    if (!delivery) return;
    delivery.date = date;
    delivery.datePicked = !!date;
    this.reprice();
  }

  /** Gives every drop with no ship-to this one; a rep's own choices are kept. */
  public fillShipTo(shipTo: string): void {
    const empty = this.deliveryList.filter((delivery) => !delivery.shipTo);
    if (empty.length === 0) return;
    empty.forEach((delivery) => (delivery.shipTo = shipTo));
    this.reprice();
  }

  /** Every drop to one address; true when one moved. */
  public shipAllTo(shipTo: string): boolean {
    const moved = this.deliveryList.filter((d) => d.shipTo !== shipTo);
    moved.forEach((delivery) => (delivery.shipTo = shipTo));
    if (moved.length > 0) this.reprice();
    return moved.length > 0;
  }

  public setDeliveryShipTo(sequence: number, shipTo: string | null): void {
    const delivery = this.deliveryList.find((d) => d.sequence === sequence);
    if (!delivery) return;
    delivery.shipTo = shipTo;
    // Ship-to does not affect pricing today, but re-pricing keeps one code path and the
    // result's provenance honest if it ever becomes a pricing subject.
    this.reprice();
  }

  public getHeader(attribute: string): AppUiFieldValue | undefined {
    return this.headerValues.get(attribute);
  }

  /**
   * Writes a header value. Returns true when it actually changed, so a caller can skip a
   * repaint. Header fields take no part in pricing, so this does not re-price.
   */
  public setHeader(attribute: string, value: AppUiFieldValue | null): boolean {
    const current = this.headerValues.get(attribute);
    if (value === null || value.value === null || value.value === '') {
      return this.headerValues.delete(attribute);
    }
    if (current?.value === value.value && current?.label === value.label)
      return false;
    this.headerValues.set(attribute, { ...value });
    return true;
  }

  public toSnapshot(): OrderDraftSnapshot {
    const lineIds: Record<string, Record<number, string>> = {};
    for (const [product, bySequence] of this.lineIds)
      lineIds[product] = Object.fromEntries(bySequence);
    return {
      orderId: this.orderId,
      lineIds,
      sentLineIds: [...this.sentLineIdSet],
      asOf: this.context.asOf,
      priceListId: this.context.priceList.id,
      bundleVersion: this.context.bundleVersion ?? null,
      deliveries: this.deliveryList.map((d) => ({ ...d })),
      lines: this.buildLines(),
      header: this.header,
      schedule: this.chosenSchedule,
    };
  }

  public hasQuantityIn(sequence: number): boolean {
    for (const bySequence of this.quantities.values())
      if (bySequence.has(sequence)) return true;
    return false;
  }

  /** The schedule's count column is the order's actual number of drops, through every change. */
  private syncCount(): void {
    const schedule = this.chosenSchedule;
    if (!schedule) return;
    schedule.drops = this.deliveryList.length;
    const attribute = schedule.countColumn;
    const columns = schedule.columns;
    // A schedule that leaves the count empty keeps it so on the order: it is not that cadence's.
    if (attribute && columns && columns[attribute] !== null)
      columns[attribute] = this.deliveryList.length;
  }

  private clearQuantitiesIn(sequence: number): void {
    for (const product of [...this.quantities.keys()])
      this.writeQuantity(product, sequence, 0);
  }

  private writeQuantity(
    product: string,
    sequence: number,
    quantity: number,
  ): void {
    const normalized = Number.isFinite(quantity)
      ? Math.max(0, Math.trunc(quantity))
      : 0;
    const bySequence =
      this.quantities.get(product) ?? new Map<number, number>();

    if (normalized === 0) bySequence.delete(sequence);
    else bySequence.set(sequence, normalized);

    // Drop the product entirely once it has no quantities, so `buildLines` stays sparse and
    // a cleared row leaves no trace in the snapshot.
    if (bySequence.size === 0) this.quantities.delete(product);
    else this.quantities.set(product, bySequence);
  }

  private restoreLineIds(lineIds: OrderDraftSnapshot['lineIds']): void {
    for (const [product, bySequence] of Object.entries(lineIds)) {
      this.lineIds.set(
        product,
        new Map(
          Object.entries(bySequence).map(([sequence, id]) => [
            Number(sequence),
            id,
          ]),
        ),
      );
      // A removed delivery's line ids stay known, so its sequence is never handed out again.
      for (const sequence of Object.keys(bySequence))
        this.nextSequence = Math.max(this.nextSequence, Number(sequence) + 1);
    }
  }

  private restoreLine(line: OrderLineInput): void {
    for (const [sequence, quantity] of Object.entries(line.quantities ?? {})) {
      this.writeQuantity(line.product, Number(sequence), quantity);
    }
    if (line.manualDiscountPct != null)
      this.manualPct.set(line.product, line.manualDiscountPct);
    if (line.manualDiscountAmount != null)
      this.manualAmount.set(line.product, line.manualDiscountAmount);
  }

  private buildLines(): OrderLineInput[] {
    const lines: OrderLineInput[] = [];
    for (const [product, bySequence] of this.quantities) {
      const quantities: Record<number, number> = {};
      for (const [sequence, quantity] of bySequence)
        quantities[sequence] = quantity;
      lines.push({
        product,
        quantities,
        manualDiscountPct: this.manualPct.get(product) ?? null,
        manualDiscountAmount: this.manualAmount.get(product) ?? null,
      });
    }
    return lines;
  }

  private reprice(): void {
    if (this.batchDepth > 0) {
      this.repriceDeferred = true;
      return;
    }
    this.cached = this.computePriced();
  }

  private computePriced(): PricedOrder {
    if (this.context.fixedPricing) return this.context.fixedPricing;
    return priceOrder({
      priceList: this.context.priceList,
      lines: this.buildLines(),
      deliveries: this.deliveryList.map(
        ({ sequence, date, shipTo, isPrimary }) => ({
          sequence,
          date,
          shipTo,
          isPrimary,
        }),
      ),
      asOf: this.context.asOf,
      includeUnmapped: this.context.includeUnmapped,
      maxDiscountPct: this.context.maxDiscountPct,
      maxDiscountAmount: this.context.maxDiscountAmount,
      priorQuantityByProduct: this.context.priorQuantityByProduct,
      bundleVersion: this.context.bundleVersion ?? undefined,
    });
  }
}
